<template>
  <section class="card border-0 mt-3 h-100">
    <div class="card-body co2e-info">
      <div class="d-flex align-items-center gap-2 mb-3">
        <h6 class="mb-0">참고정보</h6>
      </div>

      <ul class="mb-4 ps-3 small text-muted">
        <li><b>단위 :</b> {{ unitText }}</li>
        <li><b>범위 :</b> {{ scopeText }}</li>
        <li v-if="isVehicle"><b>계산식 :</b> (1 / 연비[km/L]) × 연료배출계수[kgCO₂/L] = kgCO₂/km</li>
        <li><b>해석 :</b> 값이 낮을수록 동일 기능 대비 환경영향이 작음</li>
      </ul>

      <div class="small fw-bold">
        {{ tableTitle }}
      </div>

      <div class="table-responsive">
        <table class="table table-sm align-middle mb-2 co2e-table">
          <thead>
            <tr>
              <th>{{ nameColLabel }}</th>
              <th class="text-end">{{ valueColLabel }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in tableRowsToShow" :key="i">
              <td>{{ r.name }}</td>
              <td class="text-end">{{ r.range }}</td>
            </tr>
          </tbody>
        </table>

        <div class="w-100 text-end t-from" v-if="sourceToShow">
          출처: {{ sourceToShow }}
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "Co2Info",
  props: {
    /** 'material' | 'product' | 'vehicle' */
    kind: {
      type: String,
      default: "material",
      validator: v => ["material", "product", "vehicle"].includes(v)
    },
    /** 표 데이터를 직접 넣고 싶으면 사용 (없으면 kind 기본값 사용) */
    rows: {
      type: Array,
      default: () => null, // [{ name:'알루미늄', range:'9 ~ 12' }, ...]
    },
    /** 출처 문구 커스텀 */
    source: {
      type: String,
      default: ""
    }
  },
  computed: {
    isMaterial() { return this.kind === "material"; },
    isProduct()  { return this.kind === "product"; },
    isVehicle()  { return this.kind === "vehicle"; },

    unitText() {
      if (this.isVehicle) return "kgCO₂ / km (주행 시 배출량)";
      return this.isMaterial ? "kgCO₂e / kg (이산화탄소 환산량)"
                             : "kgCO₂e / 개 (제품 1개 기준)";
    },
    scopeText() {
      if (this.isVehicle) return "주행(연료 연소 직접배출, Tank-to-Wheel)";
      return this.isMaterial ? "원재료의 생산"
                             : "제품 조립 및 포장(물류 이전 단계)";
    },
    tableTitle() {
      if (this.isVehicle) return "연료별 배출계수 (대략 / 1L 기준)";
      return this.isMaterial
        ? "대표 재료 배출계수 (참고치 / 1kg 기준)"
        : "대표 조립/포장 공정 배출량 (참고치 / 1개 기준)";
    },
    nameColLabel() {
      if (this.isVehicle) return "연료";
      return this.isMaterial ? "재료" : "제품/공정";
    },
    valueColLabel() {
      if (this.isVehicle) return "kgCO₂ / L";
      return this.isMaterial ? "kgCO₂e / kg" : "kgCO₂e / 개";
    },
    tableRowsToShow() {
      if (this.rows && this.rows.length) return this.rows;

      if (this.isVehicle) {
        // 🔹 요청하신 값 그대로 반영
        return [
          { name: "휘발유 (Gasoline)", range: "≈ 2.31" },
          { name: "경유 (Diesel)",    range: "≈ 2.68" },
          { name: "LPG",             range: "≈ 1.51" },
        ];
      }

      // 기본 예시 데이터 (기존 유지)
      return this.isMaterial
        ? [
            { name: "알루미늄",      range: "9 ~ 12" },
            { name: "철강(탄소강)",  range: "1.8 ~ 2.5" },
            { name: "플라스틱(PET)", range: "2.5 ~ 3.0" },
            { name: "종이(골판지)",  range: "0.7 ~ 1.0" },
            { name: "유리",          range: "1.0 ~ 1.5" },
          ]
        : [
            { name: "안경(플라스틱 프레임) 조립", range: "0.5 ~ 1.5" },
            { name: "안경(메탈 프레임) 조립",    range: "1.0 ~ 2.0" },
            { name: "소형 전자(드라이기) 조립",  range: "3 ~ 5" },
            { name: "의류(티셔츠) 포장",        range: "0.2 ~ 0.5" },
            { name: "소형가전 포장",            range: "0.3 ~ 0.8" },
          ];
    },
    sourceToShow() {
      if (this.source) return this.source;
      if (this.isVehicle) {
        return "IPCC 2019 Guidelines, 한국교통연구원 참고치(연료 연소 배출계수)";
      }
      return this.isMaterial
        ? "IPCC 2019 Guidelines, 한국 LCI DB (KEITI), ecoinvent v3.8"
        : "ISO 14040/14044, ISO 14067, KEITI DB, 제조사 공정 기준 예시";
    }
  }
};
</script>

<style scoped>
.co2e-info { 
  border: 1px solid rgba(0,0,0,.06); 
  border-radius: 12px;
}
.co2e-table thead th {
  font-size: 0.75rem;
  color: #6b7280;
  border-bottom: 1px solid rgba(0,0,0,.06);
}
.co2e-table tbody td {
  font-size: 0.78rem;
  padding-top: .4rem;
  padding-bottom: .4rem;
}
.t-from{
  font-size: 0.75rem;
  color: #6b7280;
}
li{
  font-size: 0.8rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #344767;
  margin-left: 0.25rem;
}
</style>
